/**
 * Display formatting — money, dates, and the one value the API returns that is NOT
 * a value.
 *
 * ⚠ IT LIVES UNDER `features/inquiries/` AND THE INQUIRY IS NO LONGER ITS SUBJECT.
 * Migration 44 dropped `inquiries.expected_value` and `inquiries.currency` (owner
 * ruling Q6), so this module's original caller carries no money at all. Its
 * exports did NOT go with them, because they were never only the inquiry's:
 * `formatMoney` / `isMaskedAmount` / `MASKED_AMOUNT` / `NO_VALUE` are imported by
 * `crfs`, `quotes`, `renewals`, the admin catalogue and the shared money renderer
 * in `quotes/money.tsx`. Moving the file is a rename across ~20 importers and is
 * NOT folded into a column drop; it is stated here instead so the next reader is
 * not misled by the folder.
 *
 * ONE EXPORT DID GO: `sumMoney`, which summed a Board column's amounts and
 * returned `null` when the sum would have been a lie. Its only caller was
 * `InquiryBoard`'s `ColumnFooter`, on the field that is gone, and its parameter
 * type named `expected_value` by name.
 *
 * ===========================================================================
 * A MONEY FIELD CAN COME BACK AS A REDACTION MARKER, NOT A NUMBER
 * ===========================================================================
 * `AmountsMaskingInterceptor` replaces every money field with `MASKED_AMOUNT`
 * ('•••') for a caller without `amounts.view` in the row's country
 * (`apps/api/src/common/interceptors/amounts-masking.interceptor.ts:24`).
 *
 * WHO IS ACTUALLY IN THAT POSITION. Enumerated over
 * `apps/api/src/seeds/role-matrix.json` — the file the seeds transcribe Appendix A
 * into, and which `role-permission.entity.ts` says a diff against Appendix A is a
 * test failure — AFTER the Phase 3a six-role remodel: `amounts.view` is held by
 * Country Manager, Accounts, Dev Head, Coordinator and Tally Sales, and NOT by
 * Developer or Tech Support. Both of those hold `crf.view`, and `crfs.amount` is a
 * registered money field on the CRF record and list, so the masked seat is an
 * ordinary seeded seat rather than an edge case.
 *
 * ⚠ THE EXAMPLE THAT USED TO STAND HERE WAS THE INQUIRY'S, and it is replaced
 * rather than deleted: it named Tech Support as "`inquiries.view` WITHOUT
 * `amounts.view`" reading a masked `expected_value`. That seat and that gap are
 * both still real; the FIELD is not, so the surviving instance of the same shape
 * is named instead. Anyone reachable only through a per-user `user_permissions`
 * override (`UserPermissionOverride`, which the admin screens edit) is in the same
 * position, which is why the handling below is unconditional rather than keyed to
 * a role: the seeded matrix is a DEFAULT a country admin may change without
 * touching this file.
 *
 * Everything here therefore treats a money field as `string | null` and asks
 * `isMaskedAmount` FIRST. `Number('•••')` is `NaN`, and a column that quietly
 * renders NaN — or worse, 0 — would tell a coordinator there is nothing there.
 * `format.test.ts` asserts the marker string against the API source so a change on
 * that side breaks a test here rather than showing '•••' as a number.
 */

// TYPE-ONLY, so nothing in this module's runtime surface depends on the bindings module.
import type { TallyEdition } from '../../lib/inquiries-api';

/**
 * The redaction marker, transcribed from `MASKED_AMOUNT` in the API's masking
 * interceptor. Asserted against that file by `format.test.ts`.
 */
export const MASKED_AMOUNT = '•••';

export function isMaskedAmount(value: string | null | undefined): boolean {
  return value === MASKED_AMOUNT;
}

/** What an absent value renders as. An em dash, never '0' and never a blank cell. */
export const NO_VALUE = '—';

/**
 * Digit grouping, per currency.
 *
 * Figma renders Indian amounts in the lakh grouping it actually uses — ₹1,20,000,
 * not ₹120,000 (node I12:1517;6:121 and the column footers on the Board). That is a
 * property of `en-IN`, so INR is mapped to it. Every other currency uses `en-GB`
 * because the design contains no non-INR example, and picking a per-country locale
 * with no design evidence would be inventing typography. AO and CD amounts will
 * therefore group in threes, which is correct for AOA and CDF.
 */
const GROUPING_LOCALE: Readonly<Record<string, string>> = { INR: 'en-IN' };
const DEFAULT_LOCALE = 'en-GB';

/**
 * A `DECIMAL(14,2)` string as money.
 *
 * · masked  → the marker, unchanged (never parsed);
 * · null/'' → {@link NO_VALUE};
 * · whole   → no decimals, matching every amount in the design (₹18,000);
 * · else    → two decimals, because dropping them would misstate the number.
 *
 * An unknown/blank ISO code falls back to a plain grouped number rather than
 * throwing — `Intl` rejects a bad currency code, and a formatter is not the right
 * place for a data-quality failure to become a blank screen.
 */
export function formatMoney(
  value: string | null | undefined,
  currency: string | null | undefined,
): string {
  if (isMaskedAmount(value)) return MASKED_AMOUNT;
  if (value === null || value === undefined || value.trim() === '') return NO_VALUE;

  const amount = Number(value);
  if (!Number.isFinite(amount)) return NO_VALUE;

  const code = (currency ?? '').trim().toUpperCase();
  const locale = GROUPING_LOCALE[code] ?? DEFAULT_LOCALE;
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;

  if (code.length === 3) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    } catch {
      /* fall through to the code-prefixed form below */
    }
  }

  const plain = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
  return code === '' ? plain : `${code} ${plain}`;
}

/*
 * ⚠ `sumMoney` STOOD HERE AND IS DELETED WITH THE FIELD IT SUMMED.
 *
 * It summed a Board column's amounts and returned `null` when the sum WOULD HAVE BEEN A
 * LIE — any masked row (the total would omit it silently) or more than one currency
 * (adding AOA to USD produces a number that means nothing) — and `ColumnFooter` rendered
 * "Amounts hidden" / "Mixed currencies" instead of a wrong total.
 *
 * ITS ONLY CALLER WAS THAT FOOTER, on `inquiries.expected_value`, which migration 44
 * drops. It is not kept "in case another list wants a total": its parameter type named
 * `expected_value` and `currency` by name, so a second caller could not have used it
 * without a rewrite, and a helper kept alive for a column that no longer exists is the
 * mirror this codebase keeps deleting. `format.test.ts`'s three `sumMoney` cases go with
 * it, WITH a callout — they are not silently dropped assertions, they are assertions
 * about a function that no longer has a subject.
 */

/* ──────────────────────────────────────────────────────────────────── dates ── */

function toDate(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** "23 Jul 2024" — `Table/DateCell` line 1 (node I12:1519;6:123). */
export function formatDateLong(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return NO_VALUE;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** "Jul 23" — the Kanban card's `DateChip` (node I12:4419;6:390). */
export function formatDayMonth(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return NO_VALUE;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
}

/** "4:20 PM" — the second half of the Peek activity row (node I12:6502;6:413). */
export function formatTime(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/** `<input type="date">` wants `yyyy-mm-dd` in LOCAL time, not a UTC ISO slice. */
export function toDateInputValue(iso: string | null | undefined): string {
  const date = toDate(iso);
  if (date === null) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

/**
 * A `yyyy-mm-dd` field value back to the ISO-8601 instant `next_action_at` wants.
 *
 * Midday LOCAL time, deliberately: a date-only field carries no time, and midnight
 * would shift across the date line for any user east or west of UTC — an action
 * booked for the 24th would be stored as the 23rd for a coordinator in Kinshasa.
 * Midday cannot cross a day boundary at any real offset.
 */
export function fromDateInputValue(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (year === undefined || month === undefined || day === undefined) return null;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

/* ─────────────────────────────────────────────────────── the Tally claim's words ── */

/**
 * `tally_edition`'s two stored tokens as words — migration 45, owner ruling Q9.
 *
 * ONE MAP, TWO CONSUMERS, so the create page's picker and the record page's row cannot
 * disagree about what `multi` is called. That is the whole reason it is here rather than
 * inline at either site: a label duplicated across a writer and a reader is the mirror
 * this codebase keeps deleting, and it drifts the first time somebody rewords one of them.
 *
 * ⚠ THE TOKEN IS NEVER SHOWN. `single` and `multi` are column values; a coordinator reads
 * "Single-user". Rendering the raw token would leak the schema onto a screen — the same
 * rule `stageTabLabel` follows for `INQUIRY_STAGES`.
 */
export const TALLY_EDITION_LABELS: Readonly<Record<TallyEdition, string>> = {
  single: 'Single-user',
  multi: 'Multi-user',
};

/**
 * The record row's edition, or {@link NO_VALUE} when the customer did not say.
 *
 * NULL IS THE ORDINARY ANSWER HERE, not an error state, so it gets the same em dash as
 * every other unstated fact on that panel — not a "not set" or an "unknown", which would
 * read as a gap somebody ought to close.
 */
export function tallyEditionLabel(value: TallyEdition | null | undefined): string {
  if (value === null || value === undefined) return NO_VALUE;
  return TALLY_EDITION_LABELS[value];
}
